import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, Sparkles } from 'lucide-react';
import { sendQuickGenChat } from '../../../services/api';
import { refreshWallet } from '../../../hooks/useWallet';
import type { QuickGenChatAction } from '../../../services/api';
import type { FlowQuestion, OrganizationType } from '../../../types';
import {
    type ChatMessage,
    SettingChangeCard,
    ConfirmGenerateCard,
    AskQuestionsCard,
} from './QuickGenChatCards';
import { buildRestoredChatMessages, WELCOME_MESSAGE } from '../utils/chatRestore';

// ── Main Component ────────────────────────────────────────────────────────────

interface QuickGenChatPanelProps {
  imageUrl: string;
  currentImageFileId?: string;
  name: string;
  title?: string | null;
  organizationName?: string | null;
  organizationType: OrganizationType;
  flowModelId?: string;
  questions?: FlowQuestion[];
  currentSettings: Record<string, string | boolean>;
  onSettingChange: (field: string, value: string) => void;
  onConfirmGenerate: (additionalRequirements?: string) => void;
  isGenerating: boolean;
  generationFailed?: boolean;
  /** 從圖片庫還原時要重建的對話內容；token 改變代表這是一次新的還原 */
  chatRestore?: {
    token: number;
    parentChatSessionId?: string;
    messages: { role: 'user' | 'model'; content: string }[];
  } | null;
  /** 目前這段流程的生成紀錄 id，用來把對話連結補寫回紀錄 */
  generationSessionId?: string;
  /** 對話 id 產生（或換新）時回報給上層，生成時要一併送出 */
  onChatSessionIdChange?: (chatSessionId: string) => void;
}

const QuickGenChatPanel: React.FC<QuickGenChatPanelProps> = ({
  imageUrl,
  currentImageFileId,
  name,
  title,
  organizationName,
  organizationType,
  flowModelId,
  questions,
  currentSettings,
  onSettingChange,
  onConfirmGenerate,
  isGenerating,
  generationFailed,
  chatRestore,
  generationSessionId,
  onChatSessionIdChange,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      type: 'assistant-text',
      content: WELCOME_MESSAGE,
    },
  ]);
  const [inputText, setInputText] = useState('');
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 記錄最後一次使用者送出的原始文字，供生成失敗時還原
  const lastSentTextRef = useRef<string>('');
  // 追蹤前一次的 generationFailed 狀態，避免重複觸發
  const prevGenerationFailedRef = useRef<boolean>(false);

  // Hidden context to append on next send (for cancelled setting changes)
  const cancelledContextRef = useRef<string>('');

  // 若同批 action 包含 settingChange，confirmGenerate 需等設定確認後才觸發
  const pendingGenerateRef = useRef<{ visualModifications: string } | null>(null);

  // Tracks a newly-switched image that must be reported to the backend on next send
  const pendingNewImageFileIdRef = useRef<string | undefined>(undefined);

  // Notify AI of image switch WITHOUT resetting the session
  const prevFileIdRef = React.useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevFileIdRef.current;
    const curr = currentImageFileId;
    prevFileIdRef.current = curr;

    if (prev === undefined && curr === undefined) return;
    if (prev === curr) return;

    // Only track as a switch when a previous image already existed
    // (skips the initial mount so we don't send a spurious imageFileId)
    if (curr && prev !== undefined) {
      pendingNewImageFileIdRef.current = curr;
    }

    cancelledContextRef.current = '';
    setMessages(p => [
      ...p,
      {
        id: `switch-${Date.now()}`,
        type: 'assistant-text',
        content: curr && prev
          ? '已切換圖片，將以此圖片繼續調整！'
          : '圖片已更新！繼續告訴我想怎麼調整新的圖片吧！',
      },
    ]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentImageFileId]);

  // 從圖片庫還原：重建先前的對話內容。
  // 這裡刻意把 sessionId 清空 —— 分支要有自己的對話，下一次送出時後端會複製
  // 來源訊息開一個新的，來源對話完全不受影響。
  // 宣告順序在「圖片切換」effect 之後，還原時才不會殘留切換提示。
  useEffect(() => {
    if (!chatRestore) return;
    setSessionId(undefined);
    setMessages(buildRestoredChatMessages(chatRestore.messages) as ChatMessage[]);
    pendingNewImageFileIdRef.current = undefined;
    cancelledContextRef.current = '';
    pendingGenerateRef.current = null;
    lastSentTextRef.current = '';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatRestore?.token]);

  // 生成失敗時，把最後一次送出的訊息放回輸入框並顯示錯誤提示
  useEffect(() => {
    const prev = prevGenerationFailedRef.current;
    prevGenerationFailedRef.current = !!generationFailed;

    if (!generationFailed || prev) return;

    // 加入錯誤訊息到對話
    pushMessage({
      id: `err-gen-${Date.now()}`,
      type: 'assistant-text',
      content: '生成失敗，請再試一次或調整需求後重新送出。',
    });

    // 把最後一次送出的文字還原到輸入框
    if (lastSentTextRef.current) {
      setInputText(lastSentTextRef.current);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generationFailed]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const pushMessage = (msg: ChatMessage) => {
    setMessages(prev => [...prev, msg]);
  };

  const markMessageUpdated = (id: string, patch: Partial<ChatMessage>) => {
    setMessages(prev => prev.map(m => m.id === id ? { ...m, ...patch } as ChatMessage : m));
  };

  // 將 AI 輸出的 label 反查為對應的 value（供 onSettingChange 套用）
  const resolveLabelToValue = (field: string, label: string): string => {
    const q = questions?.find(q => q.id === field);
    if (!q) return label;
    if (q.type === 'boolean') {
      if (label === '是') return 'true';
      if (label === '否') return 'false';
      return label;
    }
    if (q.options) {
      const opt = q.options.find(o => (o.label ?? o.value) === label);
      return opt?.value ?? label;
    }
    return label;
  };

  // Process actions from AI response and push event cards
  const processActions = (actions: QuickGenChatAction[]) => {
    // Group all settingChange actions into one card
    const settingChanges = actions.filter(a => a.action === 'settingChange') as Extract<QuickGenChatAction, { action: 'settingChange' }>[];
    if (settingChanges.length > 0) {
      const id = `evt-${Date.now()}-sc`;
      pushMessage({
        id,
        type: 'event-settingChange',
        changes: settingChanges.map(a => ({
          field: a.field,
          oldValue: String(currentSettings[a.field] ?? ''),
          value: resolveLabelToValue(a.field, a.label),
        })),
        confirmed: false,
        cancelled: false,
      });
    }

    const confirmAction = actions.find(a => a.action === 'confirmGenerate') as Extract<QuickGenChatAction, { action: 'confirmGenerate' }> | undefined;
    const hasSettingChange = settingChanges.length > 0;
    const hasAskQuestions = actions.some(a => a.action === 'askQuestions');

    if (confirmAction) {
      const id = `evt-${Date.now()}-cg`;
      if (hasAskQuestions) {
        // askQuestions 存在時，絕對不自動生成
        pushMessage({
          id,
          type: 'event-confirmGenerate',
          visualModifications: confirmAction.visualModifications,
          confirmed: false,
        });
      } else if (hasSettingChange) {
        // 有待確認的設定變更，先暫存，等使用者按下確認後再觸發
        pendingGenerateRef.current = { visualModifications: confirmAction.visualModifications };
        pushMessage({
          id,
          type: 'event-confirmGenerate',
          visualModifications: confirmAction.visualModifications,
          confirmed: false,
        });
      } else {
        // 無其他待處理事件，立即觸發生成
        pushMessage({
          id,
          type: 'event-confirmGenerate',
          visualModifications: confirmAction.visualModifications,
          confirmed: true,
        });
        onConfirmGenerate(confirmAction.visualModifications);
      }
    }

    // askQuestions
    const askAction = actions.find(a => a.action === 'askQuestions') as Extract<QuickGenChatAction, { action: 'askQuestions' }> | undefined;
    if (askAction) {
      pushMessage({
        id: `evt-${Date.now()}-aq`,
        type: 'event-askQuestions',
        questions: askAction.questions,
        answered: false,
      });
    }
    // 'info' needs no card — text bubble is enough
  };

  const doSend = async (text: string) => {
    if (!text.trim() || isTyping || isGenerating) return;

    // 記錄本次送出的文字，供生成失敗時還原
    lastSentTextRef.current = text;

    // Build final message: prepend hidden cancel context if present
    const cancelContext = cancelledContextRef.current;
    cancelledContextRef.current = '';
    const finalText = cancelContext ? `${cancelContext}\n\n${text}` : text;

    // Show user's original text in chat (without hidden context)
    const userMsgId = `user-${Date.now()}`;
    pushMessage({ id: userMsgId, type: 'user', content: text });
    setIsTyping(true);

    try {
      const params: Parameters<typeof sendQuickGenChat>[0] = {
        message: finalText,
        // 畫面上顯示的是使用者原本打的字，還原時也要看到同一句（不含隱藏的系統註記）
        displayMessage: text,
      };
      if (sessionId) {
        params.sessionId = sessionId;
        // If the user switched images since the last message, report the new image
        const pendingFileId = pendingNewImageFileIdRef.current;
        if (pendingFileId) {
          params.imageFileId = pendingFileId;
          pendingNewImageFileIdRef.current = undefined;
        }
      } else {
        params.imageUrl = imageUrl;
        params.name = name;
        params.title = title;
        params.organizationName = organizationName;
        params.organizationType = organizationType;
        if (flowModelId) params.flowModelId = flowModelId;
        params.settings = currentSettings;
        if (currentImageFileId) params.imageFileId = currentImageFileId;
        // 分支：新對話以來源對話的訊息開頭（只在建立新對話時帶）
        if (chatRestore?.parentChatSessionId) {
          params.parentChatSessionId = chatRestore.parentChatSessionId;
        }
      }
      // 讓後端把對話連結補寫回生成紀錄 —— 先前遺失的正是這條線
      if (generationSessionId) params.generationSessionId = generationSessionId;

      const response = await sendQuickGenChat(params);
      setSessionId(response.sessionId);
      onChatSessionIdChange?.(response.sessionId);
      refreshWallet();

      pushMessage({
        id: `ai-${Date.now()}`,
        type: 'assistant-text',
        content: response.text,
      });

      processActions(response.actions);
    } catch (err) {
      console.error('[QuickGenChatPanel] send error:', err);
      pushMessage({
        id: `err-${Date.now()}`,
        type: 'assistant-text',
        content: '抱歉，發生錯誤，請稍後再試。',
      });
    } finally {
      setIsTyping(false);
    }
  };

  const handleSend = () => {
    const text = inputText.trim();
    if (!text) return;
    setInputText('');
    doSend(text);
  };

  const handleQuestionSubmit = (msgId: string, combined: string) => {
    markMessageUpdated(msgId, { answered: true });
    setInputText('');
    doSend(combined);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const renderMessage = (msg: ChatMessage) => {
    switch (msg.type) {
      case 'user':
        return (
          <div key={msg.id} className="flex justify-end">
            <div className="max-w-[80%] bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm px-3.5 py-2.5 rounded-2xl rounded-br-md shadow-sm">
              {msg.content}
            </div>
          </div>
        );

      case 'assistant-text':
        return (
          <div key={msg.id} className="flex gap-2 items-start">
            <div className="shrink-0 w-7 h-7 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center mt-0.5">
              <Bot size={14} className="text-indigo-500 dark:text-indigo-400" />
            </div>
            <div className="max-w-[80%] bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm px-3.5 py-2.5 rounded-2xl rounded-bl-md shadow-sm whitespace-pre-wrap">
              {msg.content}
            </div>
          </div>
        );

      case 'event-settingChange':
        return (
          <div key={msg.id} className="flex gap-2 items-start">
            <div className="shrink-0 w-7 h-7 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center mt-0.5">
              <Sparkles size={14} className="text-amber-500 dark:text-amber-400" />
            </div>
            <SettingChangeCard
              msg={msg}
              questions={questions}
              onApply={(id) => {
                const target = messages.find(m => m.id === id);
                if (target?.type === 'event-settingChange') {
                  target.changes.forEach(c => onSettingChange(c.field, c.value));
                }
                markMessageUpdated(id, { confirmed: true });

                // 若同批有待執行的 confirmGenerate，確認設定後立即觸發生成
                const pending = pendingGenerateRef.current;
                if (pending) {
                  pendingGenerateRef.current = null;
                  setMessages(prev => prev.map(m =>
                    m.type === 'event-confirmGenerate' && !m.confirmed
                      ? { ...m, confirmed: true } as ChatMessage
                      : m
                  ));
                  onConfirmGenerate(pending.visualModifications);
                } else {
                  pushMessage({
                    id: `local-${Date.now()}`,
                    type: 'assistant-text',
                    content: '設定已套用！還想調整什麼嗎？還是要直接生成？',
                  });
                }
              }}
              onCancel={(id) => {
                cancelledContextRef.current = '[系統提示：使用者取消了修改建議]';
                markMessageUpdated(id, { cancelled: true });
              }}
            />
          </div>
        );

      case 'event-confirmGenerate':
        return (
          <div key={msg.id} className="flex gap-2 items-start">
            <div className="shrink-0 w-7 h-7 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center mt-0.5">
              <Sparkles size={14} className="text-emerald-500 dark:text-emerald-400" />
            </div>
            <ConfirmGenerateCard msg={msg} isGenerating={isGenerating} />
          </div>
        );

      case 'event-askQuestions':
        return (
          <div key={msg.id} className="flex gap-2 items-start">
            <div className="shrink-0 w-7 h-7 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center mt-0.5">
              <Bot size={14} className="text-indigo-500 dark:text-indigo-400" />
            </div>
            <AskQuestionsCard
              msg={msg}
              onSubmit={handleQuestionSubmit}
              isTyping={isTyping}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-white/90 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-3xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
        <div className="p-1.5 bg-indigo-500/10 rounded-lg">
          <Bot size={16} className="text-indigo-500 dark:text-indigo-400" />
        </div>
        <span className="font-bold text-sm text-slate-700 dark:text-slate-200">AI 對話助理</span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-0">
        {messages.map(msg => renderMessage(msg))}

        {isTyping && (
          <div className="flex gap-2 items-start">
            <div className="shrink-0 w-7 h-7 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
              <Bot size={14} className="text-indigo-500 dark:text-indigo-400" />
            </div>
            <div className="bg-slate-100 dark:bg-slate-800 px-4 py-3 rounded-2xl rounded-bl-md">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:0ms]" />
                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:150ms]" />
                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:300ms]" />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="shrink-0 px-3 py-3 border-t border-slate-100 dark:border-slate-800">
        <div className="flex gap-2 items-end">
          <textarea
            ref={textareaRef}
            value={inputText}
            onChange={e => {
              setInputText(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
            }}
            onKeyDown={handleKeyDown}
            placeholder="告訴我你想怎麼調整..."
            rows={1}
            disabled={isTyping || isGenerating}
            className="flex-1 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-700 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none disabled:opacity-50 min-h-[38px] max-h-[100px] overflow-y-auto"
          />
          <button
            onClick={handleSend}
            disabled={!inputText.trim() || isTyping || isGenerating}
            className="shrink-0 w-9 h-9 bg-indigo-600 text-white rounded-xl flex items-center justify-center hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            <Send size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickGenChatPanel;
